import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Menu, X } from "lucide-react";
import { useI18n } from "../../hooks/useI18n";
import { useBusinessPageData } from "../../hooks/useBusinessPageData";
import { useInstitutionalOrganization } from "../../hooks/useInstitutionalOrganization";
import type { OrganizationPageData } from "../../presentation/organizations/organizationPage.types";
import LanguageSelector from "./LanguageSelector";
import { getBusinessPath } from "../../routes/businessRoutes";

const useScrollDirection = () => {
  const [scrollDirection, setScrollDirection] = useState<"up" | "down" | null>(null);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrolledToTop, setScrolledToTop] = useState(true);

  useEffect(() => {
    const updateScrollDirection = () => {
      const scrollY = window.scrollY;
      setScrolledToTop(scrollY <= 10);

      const direction = scrollY > lastScrollY ? "down" : "up";
      if (direction !== scrollDirection && Math.abs(scrollY - lastScrollY) > 5) {
        setScrollDirection(direction);
      }

      setLastScrollY(scrollY >= 0 ? scrollY : 0);
    };

    window.addEventListener("scroll", updateScrollDirection);
    return () => window.removeEventListener("scroll", updateScrollDirection);
  }, [lastScrollY, scrollDirection]);

  return { scrollDirection, scrolledToTop };
};

interface NavbarViewProps {
  businesses: OrganizationPageData[];
  currentBusiness?: OrganizationPageData;
}

export const NavbarView: React.FC<NavbarViewProps> = ({ businesses, currentBusiness }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { businessId } = useParams<{ businessId?: string }>();
  const { pathname } = useLocation();
  const { scrollDirection, scrolledToTop } = useScrollDirection();
  const { language, setPreference, text } = useI18n();
  const institutional = useInstitutionalOrganization();

  const toggleMenu = () => setIsOpen((current) => !current);
  const toggleDropdown = () => setDropdownOpen((current) => !current);
  const closeDropdown = () => setDropdownOpen(false);

  const currentLogo = businessId ? currentBusiness?.logo : institutional?.logo;

  const currentName = businessId ? currentBusiness?.name : institutional?.name;

  const urlPrefix = currentBusiness ? getBusinessPath(currentBusiness.id) : "";
  const navbarVisible = scrollDirection === "up" || scrolledToTop;
  const isActive = (path: string) => pathname === path;
  const desktopLinkClass = (path: string) =>
    `relative whitespace-nowrap rounded-ui-sm px-2.5 py-2 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
      isActive(path)
        ? "bg-primary/10 text-primary after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:bg-primary"
        : "text-foreground/75 hover:bg-accent hover:text-primary"
    }`;
  const mobileLinkClass = (path: string) =>
    `block rounded-ui-sm px-3 py-2.5 font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
      isActive(path) ? "bg-primary/10 text-primary ring-1 ring-primary/15" : "text-foreground/80 hover:bg-accent hover:text-primary"
    }`;

  const languageControl = <LanguageSelector language={language} onSelect={setPreference} />;

  return (
    <nav
      className={`fixed z-50 w-full border-b border-border/80 bg-surface/95 shadow-ui-card backdrop-blur-md transition-transform duration-200 motion-reduce:transition-none ${
        navbarVisible ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between gap-3">
          <div className="min-w-0 flex-1 lg:max-w-[17rem] xl:max-w-sm">
            <Link to={urlPrefix || "/"} className="flex min-w-0 items-center gap-2.5 rounded-ui-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <img
                className="h-9 w-11 flex-none object-contain"
                src={currentLogo || "/assets/images/placeholder.jpg"}
                alt={currentName}
              />
              <span className="min-w-0 text-base font-bold leading-tight text-primary-dark sm:text-lg xl:whitespace-nowrap">
                {currentName}
              </span>
            </Link>
          </div>

          <div className="hidden flex-none items-center gap-0.5 lg:flex">
            <Link to={urlPrefix || "/"} aria-current={isActive(urlPrefix || "/") ? "page" : undefined} className={desktopLinkClass(urlPrefix || "/")}>
              {text.nav.home}
            </Link>
            <Link
              to={`${urlPrefix}/quienes-somos`}
              aria-current={isActive(`${urlPrefix}/quienes-somos`) ? "page" : undefined}
              className={desktopLinkClass(`${urlPrefix}/quienes-somos`)}
            >
              {text.nav.about}
            </Link>

            {businessId ? (
              <>
                <Link
                  to={`${urlPrefix}/galeria`}
                  aria-current={isActive(`${urlPrefix}/galeria`) ? "page" : undefined}
                  className={desktopLinkClass(`${urlPrefix}/galeria`)}
                >
                  {text.nav.gallery}
                </Link>
                <Link
                  to={`${urlPrefix}/productos`}
                  aria-current={isActive(`${urlPrefix}/productos`) ? "page" : undefined}
                  className={desktopLinkClass(`${urlPrefix}/productos`)}
                >
                  {text.nav.products}
                </Link>
              </>
            ) : (
              <Link to="/galeria" aria-current={isActive("/galeria") ? "page" : undefined} className={desktopLinkClass("/galeria")}>
                {text.nav.gallery}
              </Link>
            )}

            <div className="relative">
              <button
                type="button"
                onClick={toggleDropdown}
                aria-expanded={dropdownOpen}
                aria-haspopup="menu"
                className="flex items-center whitespace-nowrap rounded-ui-sm px-2.5 py-2 text-sm font-medium text-foreground/75 transition-colors duration-200 hover:bg-accent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {text.nav.businesses}
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>

              {dropdownOpen && (
                <div
                  role="menu"
                  className="absolute right-0 z-10 mt-2 w-64 overflow-hidden rounded-ui-md border border-border bg-popover p-1.5 shadow-ui-overlay"
                  onMouseLeave={closeDropdown}
                >
                  <div className="py-1">
                    {businesses.map((business) => (
                      <Link
                        key={business.id}
                        to={getBusinessPath(business.id)}
                        role="menuitem"
                        className="block rounded-ui-sm px-3 py-2.5 text-sm text-foreground/80 transition-colors duration-200 hover:bg-accent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={closeDropdown}
                      >
                        {business.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to={`${urlPrefix}/contacto`}
              aria-current={isActive(`${urlPrefix}/contacto`) ? "page" : undefined}
              className={desktopLinkClass(`${urlPrefix}/contacto`)}
            >
              {text.nav.contact}
            </Link>

            {businessId && (
              <Link to="/" className="ml-1 inline-flex items-center gap-1.5 whitespace-nowrap rounded-ui-sm border border-primary/25 bg-surface px-2.5 py-2 text-sm font-medium text-primary transition-colors duration-200 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                {text.nav.backToAssociation}
              </Link>
            )}

            {languageControl}
          </div>

          <div className="flex flex-none items-center lg:hidden">
            <button
              type="button"
              onClick={toggleMenu}
              aria-expanded={isOpen}
              aria-controls="mobile-navigation"
              className="inline-flex h-11 w-11 items-center justify-center rounded-ui-sm text-foreground/80 transition-colors duration-200 hover:bg-accent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="sr-only">{text.nav.openMenu}</span>
              {isOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div id="mobile-navigation" className="max-h-[calc(100vh-3.5rem)] space-y-1 overflow-y-auto overflow-x-hidden border-t border-border bg-surface px-4 pb-4 pt-3 shadow-ui-card lg:hidden">
          <Link
            to={urlPrefix || "/"}
            aria-current={isActive(urlPrefix || "/") ? "page" : undefined}
            className={mobileLinkClass(urlPrefix || "/")}
            onClick={() => setIsOpen(false)}
          >
            {text.nav.home}
          </Link>
          <Link
            to={`${urlPrefix}/quienes-somos`}
            aria-current={isActive(`${urlPrefix}/quienes-somos`) ? "page" : undefined}
            className={`${mobileLinkClass(`${urlPrefix}/quienes-somos`)} whitespace-nowrap`}
            onClick={() => setIsOpen(false)}
          >
            {text.nav.about}
          </Link>

          {businessId ? (
            <>
              <Link
                to={`${urlPrefix}/galeria`}
                aria-current={isActive(`${urlPrefix}/galeria`) ? "page" : undefined}
                className={mobileLinkClass(`${urlPrefix}/galeria`)}
                onClick={() => setIsOpen(false)}
              >
                {text.nav.gallery}
              </Link>
              <Link
                to={`${urlPrefix}/productos`}
                aria-current={isActive(`${urlPrefix}/productos`) ? "page" : undefined}
                className={mobileLinkClass(`${urlPrefix}/productos`)}
                onClick={() => setIsOpen(false)}
              >
                {text.nav.products}
              </Link>
            </>
          ) : (
            <Link
              to="/galeria"
              aria-current={isActive("/galeria") ? "page" : undefined}
              className={mobileLinkClass("/galeria")}
              onClick={() => setIsOpen(false)}
            >
              {text.nav.gallery}
            </Link>
          )}

          <div>
            <div className="px-3 pb-1 pt-2 text-sm font-semibold text-muted-foreground">{text.nav.businesses}:</div>
            {businesses.map((business) => (
              <Link
                key={business.id}
                to={getBusinessPath(business.id)}
                className="block rounded-ui-sm py-2.5 pl-6 pr-3 text-sm text-foreground/75 transition-colors duration-200 hover:bg-accent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setIsOpen(false)}
              >
                {business.name}
              </Link>
            ))}
          </div>

          <Link
            to={`${urlPrefix}/contacto`}
            aria-current={isActive(`${urlPrefix}/contacto`) ? "page" : undefined}
            className={mobileLinkClass(`${urlPrefix}/contacto`)}
            onClick={() => setIsOpen(false)}
          >
            {text.nav.contact}
          </Link>

          {businessId && (
            <Link
              to="/"
              className="flex items-center gap-2 rounded-ui-sm border border-primary/20 bg-accent px-3 py-2.5 font-medium text-primary transition-colors duration-200 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setIsOpen(false)}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {text.nav.backToAssociation}
            </Link>
          )}

          <div className="border-t border-border pt-3">{languageControl}</div>
        </div>
      )}
    </nav>
  );
};

const StandaloneNavbar: React.FC = () => {
  const { businesses, getBusinessById } = useBusinessPageData();
  const { businessId } = useParams<{ businessId?: string }>();
  return <NavbarView businesses={businesses} currentBusiness={businessId ? getBusinessById(businessId) : undefined} />;
};

const Navbar: React.FC<Partial<NavbarViewProps>> = ({ businesses, currentBusiness }) =>
  businesses
    ? <NavbarView businesses={businesses} currentBusiness={currentBusiness} />
    : <StandaloneNavbar />;

export default Navbar;
